// src/components/LoadingScreen.jsx
// import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Hexagon } from 'lucide-react';

/* =========================================
   STYLED COMPONENTS (Main Loader)
   ========================================= */

const StyledWrapper = styled.div`
  .loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .loading-wide {
    width: 150px;
    height: 150px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }
  .color {
    background-color: #06402B;
  }

  .l1 {
    width: 15px;
    height: 65px;
    position: absolute;
    animation: move-h 1.2s infinite cubic-bezier(0.65, 0.05, 0.36, 1);
  }

  .l2 {
    width: 15px;
    height: 60px;
    position: absolute;
    transform: rotate(90deg);
    animation: move-v 1.2s infinite cubic-bezier(0.65, 0.05, 0.36, 1);
  }

  @keyframes move-h {
    0% { top: 0; opacity: 0; }
    25% { opacity: 1; }
    50% { top: 30%; opacity: 1; }
    75% { opacity: 1; }
    100% { top: 100%; opacity: 0; }
  }
  
  @keyframes move-v {
    0% { left: 0; opacity: 0; }
    25% { opacity: 1; }
    50% { left: 45%; opacity: 1; }
    75% { opacity: 1; }
    100% { left: 100%; opacity: 0; }
  }

  .animation-effect-light {
    animation: effect 0.2s 0.1s infinite linear;
  }
  .animation-effect-light-d {
    animation: effect 0.3s 0.2s infinite linear;
  }
  .animation-effect-rot {
    animation: rot 0.8s infinite cubic-bezier(0.65, 0.05, 0.36, 1);
  }
  .animation-effect-scale {
    animation: scale 0.8s infinite cubic-bezier(0.65, 0.05, 0.36, 1);
  }

  @keyframes effect {
    0% { opacity: 0; }
    50% { opacity: 1; }
    100% { opacity: 0; }
  }
  
  @keyframes rot {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes scale {
    0% { scale: 1; }
    50% { scale: 1.9; }
    100% { scale: 1; }
  }
  
  .e1 {
    width: 1px;
    height: 40px;
    opacity: 0.3;
    position: absolute;
    top: 0;
    left: 8%;
  }
  .e2 {
    width: 60px;
    height: 1px;
    opacity: 0.8;
    position: absolute;
    top: 8%;
    left: 0;
  }
  .e3 {
    position: absolute;
    top: 10%;
    left: 12%;
    font-family: system-ui, -apple-system, sans-serif;
    font-weight: 900;
    font-size: 18px;
    color: #06402B;
  }
  .e4 {
    width: 1px;
    height: 40px;
    opacity: 0.3;
    position: absolute;
    top: 90%;
    right: 10%;
  }
  .e5 {
    width: 40px;
    height: 1px;
    opacity: 0.3;
    position: absolute;
    top: 100%;
    right: 0;
  }
  .e6 {
    position: absolute;
    top: 100%;
    right: 0;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 32px;
    color: #06402B;
  }
  .e7 {
    width: 1px;
    height: 20px;
    position: absolute;
    bottom: 0;
    left: 0;
    transform: rotate(45deg);
    animation: height 1s infinite cubic-bezier(0.65, 0.05, 0.36, 1);
  }
  
  @keyframes height {
    0% { bottom: 0%; left: 0%; height: 0px; }
    25% { height: 90px; }
    50% { bottom: 100%; left: 100%; height: 90px; }
    75% { height: 0px; }
    100% { bottom: 0%; left: 0%; height: 0px; }
  }
  
  .e8 {
    width: 20px;
    height: 1px;
    position: absolute;
    bottom: 50%;
    left: 0;
    animation: width 1.5s infinite cubic-bezier(0.65, 0.05, 0.36, 1);
  }
  
  @keyframes width {
    0% { left: 0%; width: 0px; }
    50% { left: 100%; width: 90px; }
    100% { left: 0%; width: 0px; }
  }
`;

/* =========================================
   MAIN EXPORTS
   ========================================= */

/**
 * Full Screen Loader - Updated with Geometric Animation
 */
export const FullScreenLoader = ({ message = "Loading GiveAura..." }) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#FBFCF6]">
      <StyledWrapper>
        <div className="loading">
          <div className="loading-wide">
            <div className="l1 color" />
            <div className="l2 color" />
            <div className="e1 color animation-effect-light" />
            <div className="e2 color animation-effect-light-d" />
            <div className="e3 animation-effect-rot">X</div>
            <div className="e4 color animation-effect-light" />
            <div className="e5 color animation-effect-light-d" />
            <div className="e6 animation-effect-scale">*</div>
            <div className="e7 color" />
            <div className="e8 color" />
          </div>
        </div>
      </StyledWrapper>
      
      <div className="mt-12 text-center">
        <h2 className="text-lg font-medium text-[#06402B] tracking-wide">
          {message}
        </h2>
      </div>
    </div>
  );
};

/**
 * Page Transition Loader - Matching Style
 */
export const PageTransitionLoader = () => {
  return (
    <div className="fixed inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center z-40 transition-opacity duration-300">
      <StyledWrapper>
        <div className="loading">
          <div className="loading-wide" style={{ width: '80px', height: '80px', transform: 'scale(0.6)' }}>
            <div className="l1 color" />
            <div className="l2 color" />
            <div className="e3 animation-effect-rot">X</div>
          </div>
        </div>
      </StyledWrapper>
    </div>
  );
};

/**
 * Typography Loader (Alternative)
 */
export const TypeLoader = ({ text = "GIVEAURA" }) => {
  const letters = text.split('');
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#06402B]">
      <div className="flex items-center gap-1">
        {letters.map((letter, i) => (
          <span
            key={i}
            className="text-4xl md:text-6xl font-bold text-white inline-block animate-letter-reveal"
            style={{ 
              animationDelay: `${i * 0.08}s`,
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}
          >
            {letter}
          </span>
        ))}
        <div className="w-2 h-2 bg-emerald-400 rounded-full ml-2 animate-blink" />
      </div>
    </div>
  );
};

/**
 * Data Loader
 */
export const DataLoader = ({ rows = 5 }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/90 backdrop-blur-sm">
      <div className="w-full max-w-md px-8 space-y-4">
        <div className="flex items-center gap-3 mb-8">
          <Hexagon className="w-6 h-6 text-[#06402B] animate-rotate-hex" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-[#06402B] tracking-wider uppercase">Loading Data</span>
        </div>
        
        {[...Array(rows)].map((_, i) => (
          <div 
            key={i} 
            className="h-12 bg-gray-100 rounded-lg overflow-hidden relative animate-fade-in-row"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <div 
              className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-200/50 to-transparent -translate-x-full animate-shimmer"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/* =========================================
   SKELETON SCREENS
   ========================================= */

export const ProductGridSkeleton = ({ count = 8 }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6 p-6">
      {[...Array(count)].map((_, i) => (
        <div 
          key={i} 
          className="group flex flex-col gap-3 opacity-0 animate-fade-scale-in"
          style={{ animationDelay: `${i * 0.05}s`, animationFillMode: 'forwards' }}
        >
          <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-gray-100">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100" />
            <div className="absolute inset-0 opacity-50">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
            </div>
          </div>
          <div className="space-y-2 px-1">
            <div className="h-4 w-3/4 bg-gray-100 rounded-md" />
            <div className="h-3 w-1/2 bg-gray-50 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const CardSkeleton = () => (
  <div className="w-full max-w-sm p-6 rounded-2xl border border-gray-100 bg-white shadow-sm animate-pulse-subtle">
    <div className="flex items-center gap-4 mb-4">
      <div className="w-12 h-12 rounded-full bg-gray-100" />
      <div className="space-y-2 flex-1">
        <div className="h-4 w-1/3 bg-gray-100 rounded" />
        <div className="h-3 w-1/4 bg-gray-50 rounded" />
      </div>
    </div>
    <div className="space-y-3">
      <div className="h-3 w-full bg-gray-50 rounded" />
      <div className="h-3 w-5/6 bg-gray-50 rounded" />
      <div className="h-3 w-4/6 bg-gray-50 rounded" />
    </div>
  </div>
);

/* =========================================
   MICRO LOADERS
   ========================================= */

export const InlineLoader = ({ size = "default", color = "#06402B" }) => {
  const sizeClasses = {
    small: "w-4 h-4 border-2",
    default: "w-6 h-6 border-2",
    large: "w-8 h-8 border-3"
  };

  return (
    <div 
      className={`${sizeClasses[size]} rounded-full border-t-transparent animate-spin`} 
      style={{ borderColor: color, borderTopColor: 'transparent' }}
    />
  );
};

export const DotsLoader = ({ color = "#06402B" }) => (
  <div className="flex gap-1">
    {[0, 1, 2].map(i => (
      <div 
        key={i}
        className="w-1.5 h-1.5 rounded-full animate-bounce-dots"
        style={{ 
          backgroundColor: color, 
          animationDelay: `${i * 0.15}s` 
        }}
      />
    ))}
  </div>
);

/* =========================================
   STYLES COMPONENT (CSS for non-styled components)
   ========================================= */

export const LoaderStyles = () => (
  <style>{`
    @keyframes letter-reveal {
      0% { opacity: 0; transform: translateY(20px) rotateX(-90deg); }
      100% { opacity: 1; transform: translateY(0) rotateX(0); }
    }
    @keyframes blink {
      0%, 100% { opacity: 0; }
      50% { opacity: 1; }
    }
    @keyframes fade-scale-in {
      0% { opacity: 0; transform: scale(0.95); }
      100% { opacity: 1; transform: scale(1); }
    }
    @keyframes fade-in-row {
      0% { opacity: 0; transform: translateX(-10px); }
      100% { opacity: 1; transform: translateX(0); }
    }
    @keyframes rotate-hex {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes bounce-dots {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-4px); }
    }
    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    @keyframes pulse-subtle {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.8; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .animate-letter-reveal { 
      animation: letter-reveal 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
      opacity: 0;
    }
    .animate-blink { animation: blink 1.4s infinite both; }
    .animate-fade-scale-in { animation: fade-scale-in 0.5s ease-out forwards; }
    .animate-fade-in-row { animation: fade-in-row 0.4s ease-out forwards; opacity: 0; }
    .animate-rotate-hex { animation: rotate-hex 3s linear infinite; }
    .animate-bounce-dots { animation: bounce-dots 0.6s infinite; }
    .animate-shimmer { animation: shimmer 2s infinite; }
    .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
    .animate-spin { animation: spin 0.8s linear infinite; }

    @media (prefers-reduced-motion: reduce) {
      .animate-letter-reveal,
      .animate-blink,
      .animate-fade-scale-in,
      .animate-fade-in-row,
      .animate-rotate-hex,
      .animate-bounce-dots,
      .animate-shimmer,
      .animate-pulse-subtle,
      .animate-spin {
        animation: none;
      }
      .animate-letter-reveal { opacity: 1; }
      .animate-fade-scale-in { opacity: 1; }
      .animate-fade-in-row { opacity: 1; }
    }
  `}</style>
);

const LoadingComponents = {
  FullScreenLoader,
  PageTransitionLoader,
  TypeLoader,
  DataLoader,
  ProductGridSkeleton,
  CardSkeleton,
  InlineLoader,
  DotsLoader,
  LoaderStyles
};

export default LoadingComponents;